import { ampli } from "@/ampli"
import { BorderedInput, Text } from "@/elements"
import { JobPostingEmploymentType, JobPostingPayDisplay, JobPostingPayRate, JobPostingPayType } from "@/gql/graphql"
import { decodeGraphqlNodeId } from "@/tracking"
import { formatCurrencyLocale } from "@/utils/formatCurrency"
import clsx from "clsx"
import { useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { PayFrequencyDropdown } from "../../../app/jobs/components/ApplyToJob/PayFrequencyDropdown"
import { InputErrorText } from "../InputErrorText"
import { PostAJobModalFormValues } from "./types"

const amountRegex = /^(?:[1-9]\d{0,2}(?:,\d{3})*|0)?(?:\.\d{1,2})?$/

interface Props {
  jobPostId: string
}

export const PayTypeInput: React.FC<Props> = ({ jobPostId }) => {
  const {
    control,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useFormContext<PostAJobModalFormValues>()
  const payType = watch("payType")
  const rateFrequency = watch("rateFrequency")
  const payDisplay = watch("payDisplay")

  const jobType = watch("jobType")

  const [minFocused, setMinFocused] = useState(false)
  const [maxFocused, setMaxFocused] = useState(false)

  // If job is unpaid we hide the pay type input
  if (jobType === JobPostingEmploymentType.Collaborative) {
    return null
  }
  return (
    <div className="flex flex-col">
      <div>
        <Text variant="body2" className="my-4 text-fg-2 sm:my-0">
          Pay type
        </Text>
        <div className="mt-2 flex w-fit flex-row flex-wrap rounded-md bg-bg-2">
          {[JobPostingPayDisplay.Exact, JobPostingPayDisplay.Range].map(type => {
            const selected = payDisplay === type || payDisplay === type
            return (
              <div
                style={{
                  boxShadow: selected ? "0px 4px 24px 0px rgba(0, 0, 0, 0.10)" : "none",
                }}
                className={clsx("m-[5px] cursor-pointer rounded-sm px-[15px] py-1", selected && "bg-bg-1")}
                tabIndex={0}
                key={type}
                onClick={() => {
                  ampli.postAJobSetDetail({
                    job_post_field: "jobType",
                    job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
                    job_post_field_value: type,
                  })
                  setError("budgetAmount", {
                    message: undefined,
                  })
                  setError("budgetMaxAmount", {
                    message: undefined,
                  })
                  setValue("payDisplay", type)
                }}>
                <Text variant="label">{type === JobPostingPayDisplay.Exact ? "Fixed" : "Range"}</Text>
              </div>
            )
          })}
        </div>
      </div>
      <div className="mt-6 rounded-md bg-[#F6F6F6] px-5 py-4 sm:px-6">
        {payDisplay === JobPostingPayDisplay.Exact && (
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row">
              <div className="flex-1">
                <Controller
                  name="budgetAmount"
                  control={control}
                  rules={{
                    required: "Fee amount is required",
                    validate: {
                      nonZero: ({ value }) => value !== 0 || "Amount cannot be zero",
                      validNumber: value => {
                        // Allow empty values (not required), but validate if there's input
                        if (value?.display === "" || value?.display == null) {
                          return true // Field is not required, so allow empty values
                        }
                        return amountRegex.test(value.display) || "Please enter a valid amount"
                      },
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <div className="flex flex-1 flex-col">
                        <Text variant="label">Rate</Text>

                        <div className="relative mt-2 flex flex-col">
                          <BorderedInput
                            {...field}
                            value={field.value?.display}
                            onChange={e => {
                              const rawValue = e.target.value
                              const formattedDisplay = formatCurrencyLocale(rawValue.toString(), { decimals: 0 })
                              const numericValue = parseFloat(rawValue.replace(/,/g, "")) || null
                              if (numericValue && numericValue > 999999) {
                                setError("budgetAmount", {
                                  type: "manual",
                                  message: "Rate cannot be greater than 999,999",
                                })
                                return
                              }
                              field.onChange({
                                display: formattedDisplay,
                                value: numericValue,
                              })
                            }}
                            className={"w-full bg-bg-1 pl-[32px]"}
                            placeholder="Add rate"
                          />
                          <Text className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-3">$</Text>
                        </div>
                      </div>
                    </>
                  )}
                />
              </div>
              <div className="mx-4 w-px shrink-0 bg-bg-3" />
              <div className="flex-1">
                <div className="flex flex-1 flex-col">
                  <Text variant="label">Frequency</Text>
                  <PayFrequencyDropdown
                    className="bg-bg-1"
                    value="Hour" //
                    error="asd" //
                    onChange={() => {}} //
                    setError={() => {}} //
                    payRateFrequency={rateFrequency}
                    setPayRateFrequency={(payRateFrequency: JobPostingPayRate | undefined) =>
                      setValue("rateFrequency", payRateFrequency)
                    }
                    payType={payType}
                    setPayType={(jobPostingPayType: JobPostingPayType | undefined) =>
                      setValue("payType", jobPostingPayType)
                    }
                  />
                </div>
              </div>
            </div>
            {errors?.budgetAmount?.message && <InputErrorText text={errors.budgetAmount.message} />}
          </div>
        )}
        {payDisplay === JobPostingPayDisplay.Range && (
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-col gap-y-4 sm:flex-row">
              <div className="flex flex-1 flex-col">
                <div className="flex flex-1 flex-row">
                  <Controller
                    name="budgetAmount"
                    control={control}
                    rules={{
                      required: "Fee amount is required",
                      validate: {
                        nonZero: ({ value }) => value !== 0 || "Amount cannot be zero",
                        validNumber: value => {
                          // Allow empty values (not required), but validate if there's input
                          if (value?.display === "" || value?.display == null) {
                            return true // Field is not required, so allow empty values
                          }
                          return amountRegex.test(value.display) || "Please enter a valid amount"
                        },
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <div className="flex flex-1 flex-col">
                          <Text variant="label" className="">
                            Min
                          </Text>

                          <div className="relative mt-2 flex flex-col">
                            <BorderedInput
                              {...field}
                              value={field.value?.display}
                              onFocus={() => setMinFocused(true)}
                              onBlur={() => setMinFocused(false)}
                              onChange={e => {
                                const rawValue = e.target.value
                                const formattedDisplay = formatCurrencyLocale(rawValue.toString(), { decimals: 0 })
                                const numericValue = parseFloat(rawValue.replace(/,/g, "")) || null
                                if (numericValue && numericValue > 999998) {
                                  setError("budgetAmount", {
                                    type: "manual",
                                    message: "Min cannot be greater than 999,998",
                                  })
                                  return
                                }
                                field.onChange({
                                  display: formattedDisplay,
                                  value: numericValue,
                                })
                              }}
                              className={"w-full bg-bg-1 pl-[32px]"}
                              placeholder="0"
                            />
                            <Text className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-3">$</Text>
                          </div>
                        </div>
                      </>
                    )}
                  />
                  <div className="relative flex h-[48px] w-[9px] flex-col justify-center self-end">
                    <div className="absolute top-1/2 z-10 h-4 w-[11px] -translate-y-1/2 translate-x-[-1px] bg-bg-1" />
                    <div className="z-20 flex h-[0px] flex-row gap-0">
                      <div
                        className={clsx(
                          "z-2 mt-[-4px] h-[4px] w-[5.5px] shrink-0 -translate-x-[1px] rounded-bl-[4px] border-b border-l bg-[#f6f6f6]",
                          minFocused ? "border-fg-primary" : "border-bg-3"
                        )}
                      />
                      <div
                        className={clsx(
                          "z-2 mt-[-4px] h-[4px] w-[5.5px] shrink-0 -translate-x-[1px] rounded-br-[4px] border-b border-r bg-[#f6f6f6]",
                          maxFocused ? "border-fg-primary" : "border-bg-3"
                        )}
                      />
                    </div>
                    <div className="h-[8px]" />
                    <div className="z-20 flex h-[0px] flex-row gap-0">
                      <div
                        className={clsx(
                          "z-2 h-[4px] w-[5.5px] shrink-0 -translate-x-[1px] rounded-tl-[4px] border-l border-t bg-[#f6f6f6] pt-[1px]",
                          minFocused ? "border-fg-primary" : "border-bg-3"
                        )}
                      />
                      <div
                        className={clsx(
                          "z-2 h-[4px] w-[5.5px] shrink-0 -translate-x-[1px] rounded-tr-[4px] border-r border-t bg-[#f6f6f6] pt-[1px]",
                          maxFocused ? "border-fg-primary" : "border-bg-3"
                        )}
                      />
                    </div>
                  </div>
                  <Controller
                    name="budgetMaxAmount"
                    control={control}
                    rules={{
                      required: "Fee amount is required",
                      validate: {
                        nonZero: ({ value }) => value !== 0 || "Amount cannot be zero",
                        validNumber: value => {
                          // Allow empty values (not required), but validate if there's input
                          if (value?.display === "" || value?.display == null) {
                            return true // Field is not required, so allow empty values
                          }
                          return amountRegex.test(value.display) || "Please enter a valid amount"
                        },
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <div className="flex flex-1 flex-col">
                          <Text variant="label">Max</Text>

                          <div className="relative mt-2 flex flex-col">
                            <BorderedInput
                              {...field}
                              value={field.value?.display}
                              onFocus={() => setMaxFocused(true)}
                              onBlur={() => setMaxFocused(false)}
                              onChange={e => {
                                const rawValue = e.target.value
                                const formattedDisplay = formatCurrencyLocale(rawValue.toString(), { decimals: 0 })
                                const numericValue = parseFloat(rawValue.replace(/,/g, "")) || null
                                if (numericValue && numericValue > 999999) {
                                  setError("budgetAmount", {
                                    type: "manual",
                                    message: "Max cannot be greater than 999,999",
                                  })
                                  return
                                }
                                field.onChange({
                                  display: formattedDisplay,
                                  value: numericValue,
                                })
                              }}
                              className={"w-full bg-bg-1 pl-[32px]"}
                              placeholder="0"
                            />
                            <Text className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-3">$</Text>
                          </div>
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
              <div className="mx-4 hidden w-px shrink-0 bg-bg-3 sm:block" />
              <div className="sm:w-[125px]">
                <div className="flex flex-1 flex-col">
                  <Text variant="label">Frequency</Text>
                  <PayFrequencyDropdown
                    className="bg-bg-1"
                    value="Hour" //
                    error="" //
                    onChange={() => {}} //
                    setError={() => {}} //
                    payRateFrequency={rateFrequency}
                    setPayRateFrequency={(payRateFrequency: JobPostingPayRate | undefined) =>
                      setValue("rateFrequency", payRateFrequency)
                    }
                    payType={payType}
                    setPayType={(jobPostingPayType: JobPostingPayType | undefined) =>
                      setValue("payType", jobPostingPayType)
                    }
                  />
                </div>
              </div>
            </div>
            {errors?.budgetAmount?.message && <InputErrorText text={errors.budgetAmount.message} />}
            {errors?.budgetMaxAmount?.message && <InputErrorText text={errors.budgetMaxAmount.message} />}
          </div>
        )}
      </div>
    </div>
  )
}
